"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.useTryFn = exports.useErrorHandling = void 0;
const react_1 = __importDefault(require("react"));
const contexts_1 = require("../contexts");
/**
 * Handles any error that occurs in the application. Also handles service errors, use [`anypoint-fetch`](https://github.com/mulesoft/anypoint-fetch) for a better handling of service errors.
 *
 * @example
 *
 * ```ts
 * import fetchFactory from '@mulesoft/anypoint-fetch';
 * import { useErrorHandling } from '@mulesoft/exchange-react-hooks';
 *
 * const fetch = fetchFactory();
 *
 * const MyComponent = ({ onData }) => {
 *   const handleError = useErrorHandling();
 *
 *   async function getData() {
 *     try {
 *       const response = await fetch('http://my-site/my-page');
 *       const result = await response.json();
 *
 *       onData(result);
 *     } catch (error) {
 *       handleError(error);
 *     }
 *   }
 *
 *   React.useEffect(() => {
 *     getData();
 *   }, []);
 *
 *   ...
 * };
 * ```
 *
 * @returns A callback to handle the error
 */
function useErrorHandling() {
    return react_1.default.useContext(contexts_1.ErrorHandlingContext);
}
exports.useErrorHandling = useErrorHandling;
/**
 * Wraps a function and handle errors for that function.
 *
 * @example
 *
 * ```ts
 * import fetchFactory from '@mulesoft/anypoint-fetch';
 * import { useTryFn } from '@mulesoft/exchange-react-hooks';
 *
 * const fetch = fetchFactory();
 *
 * const MyComponent = ({ onData }) => {
 *   const tryFn = useTryFn();
 *
 *   async function getData() {
 *     const response = await fetch('http://my-site/my-page');
 *     const result = await response.json();
 *
 *     onData(result);
 *   }
 *
 *   React.useEffect(() => {
 *     tryFn(getData());
 *   }, []);
 *
 *   ...
 * };
 * ```
 *
 * @returns A callback to run your action inside and handle errors in case of any
 */
const useTryFn = () => {
    const handleError = useErrorHandling();
    const { getContext, context } = react_1.default.useContext(contexts_1.UserContext);
    return (fn, rejectOnError = false) => {
        var _a;
        return fn((_a = getContext === null || getContext === void 0 ? void 0 : getContext()) !== null && _a !== void 0 ? _a : context).catch((error) => {
            handleError(error, () => { var _a; return fn((_a = getContext === null || getContext === void 0 ? void 0 : getContext()) !== null && _a !== void 0 ? _a : context); });
            if (rejectOnError) {
                throw error;
            }
        });
    };
};
exports.useTryFn = useTryFn;
